import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormRenderer } from '../components/FormRenderer';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { FormStyles } from '../components/FormStyles';
import { BindingError } from '../components/BindingError';
import {
  LoadingState,
  ErrorState,
  NoDataState,
} from '../components/FormStateComponents';
import { useFormFlow } from '../hooks/useFormFlow';
import {
  generateCSSVariables,
  generateAdvancedCSSVariables,
  resolveTheme,
  LanguageSelection,
} from '@dynamic-flow/ui';
import {
  validateUrlBindings,
  generateBindingErrorMessage,
} from '../utils/bindingValidation';

export const PreviewFormView: React.FC = () => {
  const { flowId, lang = 'en' } = useParams<{
    flowId: string;
    lang?: string;
  }>();

  // Use the custom hook for flow loading
  const { flow, loading, error, reload } = useFormFlow(flowId, 'draft', lang);
  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState<string>(lang);

  const handlePreviewSubmit = async () => {
    alert('Form submitted successfully! (Preview mode - no data saved)');
  };

  // Loading state
  if (loading) {
    return (
      <LoadingState
        flow={flow}
        flowId={flowId}
        message="Loading form preview..."
      />
    );
  }

  // Error state
  if (error) {
    return <ErrorState error={error} title="Preview Not Available" />;
  }

  // No data state
  if (!flow) {
    return <NoDataState />;
  }

  // Validate URL bindings
  const bindingValidation = validateUrlBindings(flow);
  const bindingErrorMessage = !bindingValidation.isValid
    ? generateBindingErrorMessage(bindingValidation)
    : null;

  // Get the theme for this flow (advanced theme overrides basic theme)
  const theme = resolveTheme(flow.theme);
  const themeCSS = generateCSSVariables(theme);
  const advancedThemeCSS = generateAdvancedCSSVariables(
    flow.theme?.advanced ?? {}
  );

  const mergedCSS = `${themeCSS}\n${advancedThemeCSS}`;

  return (
    <div
      className="min-h-screen flex flex-col gap-2"
      style={{ backgroundColor: theme.colors.background.default }}
    >
      {/* Apply theme CSS variables */}
      <style dangerouslySetInnerHTML={{ __html: mergedCSS }} />

      {/* Use shared form styles component */}
      <FormStyles />

      {/* Binding Error Banner (if any) */}
      {bindingErrorMessage && (
        <BindingError flow={flow} message={bindingErrorMessage} isPreview />
      )}

      {/* Preview Banner */}
      <div
        style={{
          backgroundColor: theme.colors.background.accent,
          borderColor: theme.colors.border.light,
        }}
        className="border-b"
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: theme.colors.primary.main }}
              >
                <svg className="w-2 h-2" fill="white" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="3" />
                </svg>
              </div>
              <div>
                <h1
                  className="text-sm font-medium"
                  style={{ color: theme.colors.text.primary }}
                >
                  Preview Mode
                </h1>
                <p
                  className="text-xs"
                  style={{ color: theme.colors.text.secondary }}
                >
                  You are viewing "{flow.title}" with {theme.name} theme
                </p>
              </div>
            </div>
            <button
              onClick={() => window.history.back()}
              className="px-3 py-1 text-sm rounded-md hover:opacity-80"
              style={{
                backgroundColor: theme.colors.primary.main,
                color: theme.colors.primary.contrastText,
              }}
            >
              Exit Preview
            </button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-md mx-auto flex flex-1 min-h-0 flex-col gap-2 w-full p-4">
        <ErrorBoundary>
          <div className="self-end shrink-0">
            <LanguageSelection
              loadFlow={reload}
              value={selectedLanguage}
              onValueChange={(code) => {
                setSelectedLanguage(code);
                const currentSearch = window.location.search;
                const targetPath = `/${code}/preview/${flowId}${currentSearch}`;
                navigate(targetPath, { replace: true });
              }}
            />
          </div>
          <div className="flex-1 flex flex-col min-h-0 w-full">
            <FormRenderer
              isPreview
              flow={flow}
              onSubmit={handlePreviewSubmit}
            />
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
};
